import React from 'react';
import { t, defineMessages } from '@sm/intl';
import { useSurveyTheme } from '@sm/webassets';
import { generateMetricsAttribute } from '@sm/metrics';
import { IconColor, IconLogoGoldie } from '@wds/icons';
import classNames from 'classnames';
import { SurveyButton } from '../SurveyButton';
import useStyles from './useStyles';

const DEFAULT_COPY = defineMessages({
  GET_STARTED: {
    id: 'Footer.GetStarted',
    defaultMessage: 'Get Started',
    description: '[Type: Button][Vis.: low] - Get Started Button',
  },
  ALL_SET: {
    id: 'Footer.AllSet',
    defaultMessage: 'All set! Now create your own interactive quiz.',
    description: '[Type: Text][Vis.: low] - All Set Info Message',
  },
});

export type Props = { isGrayScale?: boolean };

function Footer({ isGrayScale = false }: Props): React.ReactElement {
  const theme = useSurveyTheme();
  const isDarkMode = theme.isDark;
  const { footerContainer, footerContent, footerContentCell, footerContentLogo, footerContentText, ctaLink } =
    useStyles({ isDarkMode });
  const opacity = isGrayScale && isDarkMode ? 0.5 : 1;
  let goldieLogoColor: IconColor;
  if (isDarkMode) {
    goldieLogoColor = isGrayScale ? 'lightMuted' : 'light';
  } else {
    goldieLogoColor = isGrayScale ? 'darkMuted' : 'primary'; // account for 'darkMuted' or 'light'
  }

  return (
    <footer className={footerContainer}>
      <div className={footerContent}>
        <div className={footerContentCell}>
          <a
            className={footerContentLogo}
            href="/?ut_source=quiz_results"
            target="_blank"
            rel="noreferrer"
            data-sm-metrics={generateMetricsAttribute({
              data: {
                actionFlow: 'smwebQuizResultsFooterLogo',
                actionType: 'quiz_results_footer_logo_click',
                button: 'quiz_results_footer_logo',
                uiTrigger: 'quiz_results_footer_logo_click_link',
                log: {
                  level: 'info',
                  message: 'smweb quiz results footer logo clicked',
                },
              },
            })}
          >
            <IconLogoGoldie size="xl" color={goldieLogoColor} opacity={opacity} title="SurveyMonkey Logo" />
          </a>
        </div>
        <div className={classNames(footerContentText, footerContentCell)}>
          <span>{t(DEFAULT_COPY.ALL_SET)}</span>
        </div>
        <div className={footerContentCell}>
          <SurveyButton
            data-testid="Footer__QuizCTA"
            size="md"
            className={ctaLink}
            target="_blank"
            href="/create/?create=scratch&quiz=true&ut_source=quiz_results"
            rel="noreferrer"
            data-sm-metrics={generateMetricsAttribute({
              data: {
                actionFlow: 'smwebQuizResultsFooterCTA',
                actionType: 'quiz_results_cta',
                button: 'quiz_results_footer_cta',
                uiTrigger: 'create_quiz_cta_link',
                log: {
                  level: 'info',
                  message: 'smweb quiz results CTA clicked',
                },
              },
            })}
          >
            {t(DEFAULT_COPY.GET_STARTED)}
          </SurveyButton>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
